/**
 * Экспорт таблиц в `.xlsx` (`corp-report-export`, UC1124). Библиотека грузится динамическим `import()` —
 * единственная новая зависимость проекта, `write-excel-file`. Пакет не публикует корневой экспорт
 * (`package.json#exports` без `"."`), поэтому используется подпуть `/browser` (фронтенд без Node.js);
 * API 4.x: `writeXlsxFile(sheetData, options).toFile(fileName)`.
 */
import dayjs from "dayjs";
/**
 * Стирает тип строки листа — так массив `sheets` в `exportExcel` может содержать листы с разными `T`
 * (проверка типов `columns`/`rows` происходит здесь, в момент вызова, до стирания).
 */
export const excelSheet = (name, columns, rows) => ({
    name,
    columns: columns,
    rows: rows,
});
export async function exportExcel(fileName, sheets) {
    const { default: writeXlsxFile } = await import("write-excel-file/browser");
    const finalFileName = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
    if (sheets.length <= 1) {
        const sheet = sheets[0];
        if (!sheet)
            return;
        await writeXlsxFile(buildSheetRows(sheet), { columns: columnWidths(sheet) }).toFile(finalFileName);
        return;
    }
    await writeXlsxFile(sheets.map((sheet) => ({
        data: buildSheetRows(sheet),
        sheet: sheet.name,
        columns: columnWidths(sheet),
    }))).toFile(finalFileName);
}
const columnWidths = (sheet) => sheet.columns.map((column) => ({ width: column.width }));
const buildSheetRows = (sheet) => {
    const headerRow = sheet.columns.map((column) => ({
        value: column.header,
        type: String,
        fontWeight: "bold",
    }));
    const dataRows = sheet.rows.map((row) => sheet.columns.map((column) => toCell(column, column.value(row))));
    return [headerRow, ...dataRows];
};
/**
 * write-excel-file считает serial как getTime()/день (UTC): Date с локальной полуночью даёт дробный serial и в Excel
 * показывается предыдущим днём. Берём календарную дату значения в локальной зоне и кладём её как UTC-полночь.
 */
const toExcelDate = (value, withTime = false) => {
    const local = dayjs(value);
    return withTime
        ? new Date(Date.UTC(local.year(), local.month(), local.date(), local.hour(), local.minute()))
        : new Date(Date.UTC(local.year(), local.month(), local.date()));
};
const toCell = (column, value) => {
    const type = column.type ?? inferType(value);
    if (type === "number")
        return { value: value === null ? undefined : Number(value), type: Number };
    if (type === "date") {
        const format = column.format ?? "dd.mm.yyyy";
        // Формат со временем (O-G3-3): часы и минуты сохраняются в serial (локальная зона → UTC-компоненты).
        return { value: value === null ? undefined : toExcelDate(value, /h/i.test(format)), type: Date, format };
    }
    return { value: value === null ? undefined : String(value), type: String };
};
const inferType = (value) => {
    if (value instanceof Date)
        return "date";
    if (typeof value === "number")
        return "number";
    return "string";
};
